import { Box, Container, VStack, Grid, GridItem, Text, Icon, HStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';

const MotionBox = motion(Box);

const ObjectionItem = ({ q, a, delay }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true, amount: 0.3 }}
    bg="white"
    p={{ base: 6, md: 8 }}
    borderRadius="lg"
    boxShadow="0 4px 12px rgba(0, 0, 0, 0.08)"
  >
    <VStack spacing={4} align="start">
      <HStack spacing={3} align="flex-start">
        <Icon as={FaCheckCircle} color="accent.green" fontSize="20px" mt={1} flexShrink={0} />
        <Text
          fontSize={{ base: 'md', md: 'lg' }}
          fontWeight="700"
          color="brand.800"
        >
          {q}
        </Text>
      </HStack>
      <Text fontSize="sm" color="gray.700" lineHeight="1.6" pl={8}>
        {a}
      </Text>
    </VStack>
  </MotionBox>
);

const GetHiredObjections = () => {
  const objections = [
    {
      q: "I'm not technical enough. Can I actually do this?",
      a: "80% of our students had zero AI background. We literally start from 'What is an API?' and build from there. If you can Google stuff and follow steps, you can do this.",
    },
    {
      q: "Is 24 weeks really enough to get a job?",
      a: "Not just get a job — 85% get offers. The first 8 weeks you learn. Weeks 9-16 you build projects (what interviews actually ask for). Last 8 weeks you interview prep. By week 24, you're ready.",
    },
    {
      q: "What if I fall behind or miss sessions?",
      a: "Recordings of every session are yours forever. Mentors hold office hours daily. You can catch up without falling through the cracks. People have taken 28-30 weeks. That's fine.",
    },
    {
      q: "Is live mentorship actually better or just marketing?",
      a: "Yeah, it's better. Recorded videos = passive. A mentor sees your code, finds the exact issue, explains WHY you made that mistake. You won't make it again. That's the difference between learning and understanding.",
    },
    {
      q: "What if I don't get a job? Do I get my money back?",
      a: "14-day full refund guarantee. Try the first 2 weeks with live mentors, real projects, the whole thing. If it's not for you, your money back. No hidden terms.",
    },
    {
      q: "Will I actually compete with CS grads for jobs?",
      a: "Yes, and you'll win. Here's why: You'll have 5-6 shipped projects they won't have. You'll know how to use Claude and Copilot (they don't). You'll interview with confidence from 100 practice rounds. Portfolio > degree in 2026.",
    },
  ];

  return (
    <Box py={{ base: 12, md: 16 }} bg="gray.50">
      <Container maxW="6xl">
        <VStack spacing={12}>
          {/* Header */}
          <VStack spacing={3} align="center" textAlign="center" maxW="2xl">
            <Text
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="900"
              color="brand.800"
            >
              Wait, But What If...?
            </Text>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
              Real questions people ask. Real answers.
            </Text>
          </VStack>

          {/* Objections Grid */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={6}
            w="full"
          >
            {objections.map((obj, idx) => (
              <GridItem key={idx}>
                <ObjectionItem q={obj.q} a={obj.a} delay={idx * 0.1} />
              </GridItem>
            ))}
          </Grid>

          {/* Bottom CTA */}
          <MotionBox
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            textAlign="center"
            pt={4}
          >
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700">
              More questions? Ask live in the webinar. We'll answer anything. 👇
            </Text>
          </MotionBox>
        </VStack>
      </Container>
    </Box>
  );
};

export default GetHiredObjections;
